import type {
  DateOperatorFilter,
  DateOperatorFilterInput,
  DropdownOptionFilter,
  FilterConfig,
  LineItemEndDateFilter,
  RangeFilter,
  ScheduleFilterInputV5,
  StringOperatorFilterInput,
  TraffickingFilters,
  UserFilter,
} from '../../apis/graphql';
import type { TraffickingFiltersInput } from '../../apis/graphql';
import { FilterInputType, FilterOperator, StringOperator, TraffickingTab } from '../../apis/graphql';
import { isAllSelectedValue } from '../../common/MultiSelectDropdown';
import { PredefinedCustomFilterId } from '../../configs/customFilters';
import { TraffickingTableName } from '../../constants';
import type { GlobalFilters } from '../../contexts/FilterContext/hooks';
import type { OperatorFilterValue } from '../../contexts/FilterContext/hooks/utils';
import {
  getLineItemStatusFiltersInput,
  getStringOperatorFilterKeys,
  isOperatorFilterValue,
  STRING_OPERATOR_FIELDS,
  wrapSaveStringOperatorField,
} from '../../contexts/FilterContext/hooks/utils';
import type { Nullable } from '../../models';
import { NO_MATCHING_IDS_ID, QueryVariablesKeys } from './constants';
import { isDateOperatorFilterFilled } from './FilterPanel/operatorQueryVariables';
import { isLineItemEndDateFilterFilled, isRangeFilterFilled } from './TableFilterBar/AdditionalFilters/utils';
import { ALL_TIME_DATE_KEY } from './TableFilterBar/DateFilter';
import type { SelectedDateValue } from './TableFilterBar/DateFilter/helpers';
import type { QueryVariables, SelectedRowIds } from './TraffickingPage/hooks';
import { DEFAULT_LIMIT } from './TraffickingPage/hooks';
import { isValidSortOption } from './TraffickingPage/hooks/utils';

/**
 * All value types that can appear as values inside a $filterOptions GQL variable.
 * Scalars: string, string[], boolean, number.
 * Structured: RangeFilter (min/max range), DateOperatorFilterInput (operator + date(s)),
 * ScheduleFilterInputV5 (schedule date range).
 */
export type FilterOptionValue =
  | string
  | string[]
  | boolean
  | number
  | RangeFilter
  | DateOperatorFilterInput
  | ScheduleFilterInputV5
  | StringOperatorFilterInput
  | null;

/** Flat key-value bag sent to GQL as the $filterOptions variable. */
export type FilterOptionsInput = Record<string, FilterOptionValue>;

export const getSelectedRowIdsFromState = (selectedRowsIds: string[] = []): Record<string, boolean> =>
  selectedRowsIds.reduce(
    (acc, id) => ({
      ...acc,
      [id]: true,
    }),
    {}
  );

export type GenerateQueryVariablesOptions<T extends TraffickingTableName> = {
  searchTerm: string;
  sortOption: NonNullable<TraffickingFilters[T]>['sortBy'] | undefined;
  accountManager: Nullable<UserFilter[]>;
  traffickerEmailList: Nullable<UserFilter[]>;
  accountExecutive: Nullable<UserFilter[]>;
  accountExecutiveList: Nullable<UserFilter[]>;
  dateFilter: SelectedDateValue;
  productType: Nullable<string[]>;
  adProductList: Nullable<string[]>;
  generalPacingStatusList: Nullable<string[]>;
  reviewStatusList: Nullable<string[]>;
  pacingRiskList: Nullable<string[]>;
  hasMisalignedSchedule: Nullable<boolean>;
  adStatusList: Nullable<string[]>;
  orderTypeList: Nullable<string[]>;
  tierNameList: Nullable<string[]>;
  priorityValueList: Nullable<string[]>;
  billableThirdPartyList: Nullable<string[]>;
  publisherList: Nullable<string[]>;
  isCoppaOrCaru: Nullable<boolean>;
  isMakegood: Nullable<boolean>;
  isAddedValue: Nullable<boolean>;
  lineItemStatusList: Nullable<string[]>;
  adTypeList: Nullable<string[]>;
  creativeTypeList: Nullable<string[]>;
  creativeReviewList: Nullable<string[]>;
  brandIdList: Nullable<DropdownOptionFilter[]>;
  excludeBrandIdList: Nullable<DropdownOptionFilter[]>;
  industryIdList: Nullable<DropdownOptionFilter[]>;
  agencyIdList: Nullable<DropdownOptionFilter[]>;
  currencyList: Nullable<string[]>;
  countryList: Nullable<string[]>;
  isRotation: Nullable<boolean>;
  isSequence: Nullable<boolean>;
  tableName: T;
  selectedRowIds: SelectedRowIds;
  campaignRowsIds?: SelectedRowIds;
  lineItemRowIds?: SelectedRowIds;
  // KATANA-1715: when false, parent-tab selection is not forwarded as campaignIdList /
  // lineItemIdList, so selecting a row no longer filters the other tabs. Defaults to true so
  // callers that don't opt in keep the pre-flag behaviour.
  scopeByParentSelection?: boolean;
  // Narrows the tab to its own selected ids, ignoring any parent tab's selection.
  showSelectedOnly?: boolean;
  doNotTraffic: Nullable<boolean>;
  lineItemTypeList: Nullable<string[]>;
  percentPacing: Nullable<RangeFilter>;
  completionsRemaining: Nullable<RangeFilter>;
  lineItemEndDate: Nullable<LineItemEndDateFilter | DateOperatorFilter>;
  sspList: Nullable<string[]>;
  customPacingApplied: Nullable<boolean>;
  costMethodList: Nullable<string[]>;
  billableEventList: Nullable<string[]>;
  distributionPartnerList: Nullable<string[]>;
  selectedCustomFilterId: Nullable<string>;
  CPM: Nullable<RangeFilter>;
  frequencyCap: Nullable<boolean>;
};

const toFilterId = (item: string | DropdownOptionFilter | UserFilter): string =>
  typeof item === 'string' ? item : item.value;

export const getDropdownOptionFilterVariables = (
  filter: Nullable<DropdownOptionFilter[] | string[]>,
  key: QueryVariablesKeys
): { [x: string]: string[] } => {
  if (!!filter?.length) {
    return { [key]: filter.map(toFilterId) };
  }

  return {};
};

export const getUserFilterQueryVariables = (
  filter: Nullable<UserFilter[] | string[]>,
  key: QueryVariablesKeys
): { [x: string]: string[] } => {
  if (!!filter?.length && !isAllSelectedValue(filter as UserFilter[])) {
    return { [key]: filter.map(toFilterId) };
  }

  return {};
};

// KATANA-1499: same as getDropdownOptionFilterVariables, but wraps the ids in the canonical
// always-CONTAINS StringOperatorFilterInput shape for the 17 migrated query fields.
export const getStringOperatorFilterVariables = (
  filter: Nullable<DropdownOptionFilter[] | string[]>,
  key: QueryVariablesKeys
): { [x: string]: StringOperatorFilterInput } => {
  if (!!filter?.length) {
    return { [key]: { operator: StringOperator.Contains, values: filter.map(toFilterId) } };
  }

  return {};
};

/**
 * Extracts and normalizes query variable values for config-driven filters
 * routable to the given tab's GQL query. Handles both FilterOption[] (searchable
 * filters) and string[] (static filters) by mapping each item to its string ID
 * via the same toFilterId logic used by getDropdownOptionFilterVariables. Scoping
 * by tab uses applicableQueries (which GQL input types accept this field) — not
 * applicableTabs (which tabs show the filter in the panel). This ensures Campaign-
 * only fields (e.g. assetTagList) are not sent to LineItem or Ad queries (400),
 * while fields that are panel-scoped to one tab but query-valid on all three are
 * correctly forwarded cross-tab.
 */
// KATANA-1509: OperatorFilterValue.operator is the UI's FilterOperator (matching every other
// operator field in GlobalFilters, e.g. MultiSelectState.operator), but the wire type
// (StringOperatorFilterInput.operator) is the GQL StringOperator — a differently-named enum that
// happens to share the CONTAINS/DOES_NOT_CONTAIN string values. Brand's operator is only ever one
// of those two (the toggle has no other option), so this is a narrowing map, not a lossy one.
const toStringOperator = (operator: OperatorFilterValue['operator']): StringOperator =>
  operator === FilterOperator.DoesNotContain ? StringOperator.DoesNotContain : StringOperator.Contains;

export const getConfigDrivenFilterVars = (
  filterConfigs: FilterConfig[],
  filters: GlobalFilters,
  tab: TraffickingTab
): Record<string, string[] | StringOperatorFilterInput> =>
  filterConfigs.reduce((acc, config) => {
    if (!config.applicableQueries?.includes(tab)) return acc;
    const value = filters[config.id as keyof GlobalFilters];
    // KATANA-1509: Brand stores its committed value in GlobalFilters as the canonical
    // `{ operator, values }` object (not a flat array — see OperatorFilterValue's doc comment),
    // since its operator is a genuine client-chosen Contains/DoesNotContain toggle that a flat
    // array can't carry. Forward it as StringOperatorFilterInput, flattening DropdownOptionFilter
    // values to bare ids — same wire shape the 17 always-CONTAINS fields below use, just with the
    // operator taken from the stored value instead of hardcoded to Contains.
    if (isOperatorFilterValue(value)) {
      if (value.values.length === 0) return acc;
      acc[config.id] = { operator: toStringOperator(value.operator), values: value.values.map(toFilterId) };
      return acc;
    }
    if (!Array.isArray(value) || value.length === 0) return acc;
    const ids = (value as (string | DropdownOptionFilter)[]).map(toFilterId);
    // KATANA-1499: config-driven directly off the entry's own inputType — no Set needed since the
    // FilterConfig object is already in hand. Keep this in sync with generateQueryVariables'
    // generic loop (which checks the equivalent stringOperatorKeys Set, since it only has flat
    // key/value pairs, not FilterConfig objects) — both paths can process the same config id
    // (config-driven filters are catalog entries too), so both must agree on the wrapped shape or
    // the later spread silently overwrites one with the other.
    acc[config.id] =
      config.inputType === FilterInputType.StringOperator ? { operator: StringOperator.Contains, values: ids } : ids;
    return acc;
  }, {} as Record<string, string[] | StringOperatorFilterInput>);

// Keys excluded from the generic spread — handled explicitly in generateQueryVariables.
//
// V1 alias keys (accountManager, accountExecutive, productType) do not match their GQL field
// names; they're resolved below with the V2 ?? V1 fallback pattern.
// lineItemSubStatusList is derived from lineItemStatusList via getLineItemStatusFiltersInput.
// traffickerEmailList / accountExecutiveList / adProductList are the V2 keys — handled
// explicitly alongside their V1 counterparts so the ?? resolution controls both.
const NON_GENERIC_FILTER_KEYS = new Set([
  'dateFilter',
  'selectedTab',
  'accountManager',
  'traffickerEmailList',
  'accountExecutive',
  'accountExecutiveList',
  'productType',
  'adProductList',
  'lineItemSubStatusList',
  'campaigns',
  'lineItems',
  'ads',
  // UI-only drag-reorder key — never sent to GQL (not a valid CampaignFilterOptionsV5 field).
  // Excluded here via NON_GENERIC_FILTER_KEYS regardless of tab gating, so it never leaks into
  // the strict CampaignFilterOptionsV5 input and breaks table queries.
  'displayOrder',
  // KATANA-1509: brandIdOperator is an OperatorFilterValue ({operator, values}), not a flat array —
  // the generic loop's isDateOperatorFilterLike check would otherwise false-positive-match it (both
  // shapes have an `operator` key and neither has `min`), silently omitting it as "unfilled" rather
  // than forwarding it. It's handled explicitly by getConfigDrivenFilterVars instead.
  'brandIdOperator',
]);

/**
 * Alert keys allowed on the generic filter loop regardless of tab (all are valid on every GQL input
 * type). Covers the legacy `pacingRiskList` Zero Delivery key, which is not a catalog id and so is
 * not covered by `catalogKeys` in getAllowedFilterKeys. If a future global control is added, include
 * it here.
 *
 * KATANA-1657: `lineItemEndDate` is included so existing shareable links and direct API callers
 * that apply the legacy comparison filter continue to work on all tabs, even though the catalog
 * entry's applicableQueries may not cover every tab.
 */
const TAB_AGNOSTIC_FILTER_KEYS = new Set([
  'hasMisalignedSchedule',
  'hasZeroDeliveryRisk',
  'pacingRiskList',
  'lineItemEndDate',
]);

/**
 * Fallback per-tab allowed key sets. Used when filterConfigs is empty (config loading
 * or errored). Mirrors the keys that GET_LINEITEMS_BY_CAMPAIGNID_LIST and GET_ADS
 * query documents accept.
 */
const LEGACY_LINE_ITEM_FILTER_KEYS = new Set([
  'pacingRiskList',
  'hasMisalignedSchedule',
  'hasZeroDeliveryRisk',
  'generalPacingStatusList',
  'reviewStatusList',
  'adStatusList',
  'orderTypeList',
  'tierNameList',
  'priorityValueList',
  'billableThirdPartyList',
  'publisherList',
  'adTypeList',
  'creativeTypeList',
  'creativeReviewList',
  'isCoppaOrCaru',
  'isMakegood',
  'isAddedValue',
  'brandIdList',
  'excludeBrandIdList',
  'agencyIdList',
  'industryIdList',
  'currencyList',
  'isRotation',
  'isSequence',
  'countryList',
  'doNotTraffic',
  'lineItemTypeList',
  'percentPacing',
  'completionsRemaining',
  'lineItemEndDate',
  'sspList',
  'customPacingApplied',
  'costMethodList',
  'billableEventList',
  'distributionPartnerList',
  'CPM',
  'frequencyCap',
]);

const LEGACY_AD_FILTER_KEYS = new Set([
  'pacingRiskList',
  'hasMisalignedSchedule',
  'hasZeroDeliveryRisk',
  'generalPacingStatusList',
  'reviewStatusList',
  'adStatusList',
  'orderTypeList',
  'tierNameList',
  'priorityValueList',
  'billableThirdPartyList',
  'publisherList',
  'adTypeList',
  'creativeTypeList',
  'creativeReviewList',
  'isCoppaOrCaru',
  'isMakegood',
  'isAddedValue',
  'brandIdList',
  'excludeBrandIdList',
  'agencyIdList',
  'industryIdList',
  'currencyList',
  'isRotation',
  'isSequence',
  'countryList',
  'doNotTraffic',
  'lineItemTypeList',
  'percentPacing',
  'completionsRemaining',
  'lineItemEndDate',
  'sspList',
  'customPacingApplied',
  'costMethodList',
  'billableEventList',
  'distributionPartnerList',
  'CPM',
  'frequencyCap',
]);

/**
 * Returns the set of generic-loop keys allowed in a given tab's filterOptions object,
 * or null if the tab is ungated.
 *
 * - Empty filterConfigs (config loading or errored): Campaign → null (ungated);
 *   LineItem / Ad → the fallback key set union TAB_AGNOSTIC_FILTER_KEYS.
 * - filterConfigs present: every tab, Campaign included, is gated to the union of
 *   catalog-routable ids plus TAB_AGNOSTIC_FILTER_KEYS.
 *
 * Gating uses applicableQueries (which GQL input types accept this field), NOT
 * applicableTabs (which tabs show the filter in the panel). This allows fields that are
 * panel-scoped to one tab but query-valid on all three to be forwarded cross-tab (e.g.
 * CPM set on the Campaign panel still narrows the Ad query). Fields whose applicableQueries
 * exclude a tab (e.g. an owner-only field with applicableQueries = [Ad]) are correctly
 * excluded from the other tabs' queries — including Campaign.
 *
 * Callers pre-compute the allowed set and pass it into generateQueryVariables so the
 * generator stays pure (no catalog awareness inside it).
 */
export const getAllowedFilterKeys = (tab: TraffickingTab, filterConfigs: FilterConfig[]): Set<string> | null => {
  if (filterConfigs.length === 0) {
    // Fallback: config loading or errored. Campaign stays ungated (CampaignFilterOptionsV5
    // is the GlobalFilters superset). LineItem/Ad get the static fallback set.
    if (tab === TraffickingTab.Campaign) return null;
    const legacyKeys = tab === TraffickingTab.LineItem ? LEGACY_LINE_ITEM_FILTER_KEYS : LEGACY_AD_FILTER_KEYS;
    return new Set([...legacyKeys, ...TAB_AGNOSTIC_FILTER_KEYS]);
  }

  // Gate every tab (Campaign included) by applicableQueries union tab-agnostic keys.
  const catalogKeys = filterConfigs
    .filter((config) => config.applicableQueries?.includes(tab))
    .map((config) => config.id);

  return new Set([...catalogKeys, ...TAB_AGNOSTIC_FILTER_KEYS]);
};

function isRangeFilterLike(v: unknown): v is RangeFilter {
  return typeof v === 'object' && v !== null && !Array.isArray(v) && ('min' in v || 'max' in v);
}

function isDateOperatorFilterLike(v: unknown): v is DateOperatorFilterInput {
  return typeof v === 'object' && v !== null && !Array.isArray(v) && 'operator' in v && !('min' in v);
}

/**
 * The filter panel has no date/schedule shortcut. The filter state still defaults `dateFilter`
 * to "Running This Month", which would otherwise be sent as `filterOptions.schedule` with no
 * visible chip or affordance to clear it (KATANA-1494). Always coerce to "All Time" so
 * `generateQueryVariables` omits `schedule` (unfiltered state).
 */
export const getEffectiveDateFilter = (_dateFilter: SelectedDateValue): SelectedDateValue => ({
  label: ALL_TIME_DATE_KEY,
  value: ALL_TIME_DATE_KEY,
});

/** Maps an array filter field to its ID strings, wrapped as a string-operator filter when the key calls for one. */
function resolveArrayFilterValue(
  key: string,
  value: unknown[],
  stringOperatorKeys: Set<string>
): FilterOptionValue | undefined {
  const ids = (value as Array<string | DropdownOptionFilter | UserFilter>).map(toFilterId).filter(Boolean);

  if (ids.length === 0) return undefined;

  return stringOperatorKeys.has(key) ? { operator: StringOperator.Contains, values: ids } : ids;
}

/**
 * Maps one raw filter field to the value `filterOptions` should carry, or `undefined` to omit the
 * key. Arrays of FilterOption/DropdownOptionFilter objects are mapped to ID strings; empty arrays,
 * null/undefined, unfilled range filters and partially-filled date operator filters are omitted.
 */
function resolveGenericFilterValue(
  key: string,
  value: unknown,
  stringOperatorKeys: Set<string>
): FilterOptionValue | undefined {
  if (value === null || value === undefined) return undefined;

  if (Array.isArray(value)) {
    return resolveArrayFilterValue(key, value, stringOperatorKeys);
  }

  if (isRangeFilterLike(value)) {
    return isRangeFilterFilled(value) ? value : undefined;
  }

  if (isDateOperatorFilterLike(value)) {
    return isDateOperatorFilterFilled(value) ? value : undefined;
  }

  // Scalar values (boolean, string, number) — include as-is.
  return value as FilterOptionValue;
}

/**
 * Builds `filterOptions` generically from the raw filter fields, excluding the blocklist. When a
 * per-tab allowed set is provided (LineItem/Ad), keys outside it are skipped; Campaign passes null
 * (ungated), which arrives here as undefined.
 */
function buildGenericFilterOptions(
  // Typed loosely because the caller's rest-destructured object carries no index signature.
  filterFields: object,
  allowedFilterKeys: Set<string> | undefined,
  stringOperatorKeys: Set<string>
): FilterOptionsInput {
  const genericFilterOptions: FilterOptionsInput = {};

  for (const [key, value] of Object.entries(filterFields)) {
    if (NON_GENERIC_FILTER_KEYS.has(key)) continue;
    if (allowedFilterKeys !== undefined && !allowedFilterKeys.has(key)) continue;

    const resolved = resolveGenericFilterValue(key, value, stringOperatorKeys);

    if (resolved !== undefined) {
      genericFilterOptions[key] = resolved;
    }
  }

  return genericFilterOptions;
}

type SelectionFilterOptionsParams = Pick<
  GenerateQueryVariablesOptions<TraffickingTableName>,
  'tableName' | 'selectedRowIds' | 'campaignRowsIds' | 'lineItemRowIds' | 'scopeByParentSelection' | 'showSelectedOnly'
>;

/** Owns the id-based filter keys: which rows are selected, and how a parent tab's selection scopes. */
function getSelectionFilterOptions({
  tableName,
  selectedRowIds,
  campaignRowsIds,
  lineItemRowIds,
  scopeByParentSelection = true,
  showSelectedOnly = false,
}: SelectionFilterOptionsParams): FilterOptionsInput {
  const selectedIds = Object.keys(selectedRowIds || {});

  // KATANA-1713: every tab narrows to its own selected ids, which the schema ignores unless
  // selectedIdList is absent. A tab with nothing selected gets the sentinel instead, so it renders
  // an empty table rather than an unnarrowed one.
  if (showSelectedOnly) {
    return { idList: selectedIds.length > 0 ? selectedIds : [NO_MATCHING_IDS_ID] };
  }

  const selectionFilterOptions: FilterOptionsInput = { selectedIdList: selectedIds };

  // KATANA-1715: skipped under the flag so selection stops narrowing the other tabs' result sets.
  if (scopeByParentSelection) {
    if (tableName === TraffickingTableName.lineItems) {
      selectionFilterOptions.campaignIdList = Object.keys(campaignRowsIds || {});
    } else if (tableName === TraffickingTableName.ads) {
      selectionFilterOptions.campaignIdList = Object.keys(campaignRowsIds || {});
      selectionFilterOptions.lineItemIdList = Object.keys(lineItemRowIds || {});
    }
  }

  return selectionFilterOptions;
}

export const generateQueryVariables = <T extends TraffickingTableName>(
  options: GenerateQueryVariablesOptions<T>,
  limit: number = DEFAULT_LIMIT,
  offset: number = 0,
  allowedFilterKeys?: Set<string>,
  // KATANA-1499 follow-up: legacy-floor default (STRING_OPERATOR_FIELDS alone) for callers that
  // don't pass this — mirrors allowedFilterKeys' fallback-to-ungated pattern. Real call sites pass
  // getStringOperatorFilterKeys(filterConfigs) so config-only fields (e.g. buyerNameList) are
  // wrapped too once the catalog is loaded.
  stringOperatorKeys: Set<string> = STRING_OPERATOR_FIELDS
): QueryVariables => {
  const {
    sortOption,
    accountManager,
    traffickerEmailList,
    accountExecutive,
    accountExecutiveList,
    dateFilter,
    lineItemStatusList,
    tableName,
    selectedRowIds,
    campaignRowsIds,
    lineItemRowIds,
    // Destructured (not left in filterFields) so the boolean never leaks into FilterOptionsInput
    // via buildGenericFilterOptions.
    scopeByParentSelection = true,
    showSelectedOnly = false,
    searchTerm,
    productType,
    adProductList,
    selectedCustomFilterId,
    ...filterFields
  } = options;

  const filterOptions: FilterOptionsInput = {
    ...buildGenericFilterOptions(filterFields, allowedFilterKeys, stringOperatorKeys),
    ...getSelectionFilterOptions({
      tableName,
      selectedRowIds,
      campaignRowsIds,
      lineItemRowIds,
      scopeByParentSelection,
      showSelectedOnly,
    }),
    ...(searchTerm ? { searchTerm: searchTerm.toLowerCase() } : {}),
    ...(selectedCustomFilterId &&
    Object.values(PredefinedCustomFilterId).includes(selectedCustomFilterId as PredefinedCustomFilterId)
      ? { presetId: selectedCustomFilterId }
      : {}),
    ...(dateFilter?.label !== ALL_TIME_DATE_KEY ? { schedule: dateFilter?.value } : {}),
    // V2 keys preferred; fall back to V1 keys for legacy GlobalFilters written before the rename.
    ...getUserFilterQueryVariables(traffickerEmailList ?? accountManager, QueryVariablesKeys.traffickerEmailList),
    ...getUserFilterQueryVariables(accountExecutiveList ?? accountExecutive, QueryVariablesKeys.accountExecutiveList),
    ...getStringOperatorFilterVariables(adProductList ?? productType, QueryVariablesKeys.adProductList),
    ...getLineItemStatusFiltersInput(lineItemStatusList),
  };

  filterOptions.viewerTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const queryVariables: QueryVariables = {
    filterOptions,
    limit,
    offset,
  };

  if (isValidSortOption(tableName, sortOption)) {
    queryVariables.sort = sortOption.sort;
    queryVariables.order = sortOption.order;
  }

  return queryVariables;
};

export const convertFiltersToTraffickingFiltersInput = (
  filters: GlobalFilters,
  filterConfigs: FilterConfig[] = []
): TraffickingFiltersInput => {
  // Strip __typename (present when GlobalFilters is populated from a GQL TraffickingFilters response)
  // so it never reaches TraffickingFiltersInput, which does not define that field.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { __typename, ...filterFields } = filters as GlobalFilters & { __typename?: string };

  // For the V1/V2 alias pairs, always write V2 key names to the output. The toolbar writes to
  // V2 keys (traffickerEmailList, accountExecutiveList) and getDefaultFilters nulls the V1
  // counterparts when V2 keys are set, so the V1 fallback is for legacy shareable links that
  // stored data under the old keys. campaign-graphql normalizes both to the same field.
  const adProductIds = (filters.adProductList ?? filters.productType)?.map(toFilterId);
  const v2AliasFields = {
    traffickerEmailList: (filters.traffickerEmailList ?? filters.accountManager)?.map(toFilterId) ?? null,
    accountExecutiveList: (filters.accountExecutiveList ?? filters.accountExecutive)?.map(toFilterId) ?? null,
    adProductList: adProductIds?.length ? { operator: StringOperator.Contains, values: adProductIds } : null,
  };

  // Exclude V1 and V2 alias keys from the base spread — they are covered by v2AliasFields above.
  // Also exclude displayOrder — it is a UI-only drag-reorder key that must never reach
  // TraffickingFiltersInput (shareable-link mutation) or any strict GQL input.
  const EXCLUDED_FROM_INPUT = new Set([
    'accountManager',
    'accountExecutive',
    'productType',
    'traffickerEmailList',
    'accountExecutiveList',
    'adProductList',
    'displayOrder',
  ]);
  // KATANA-1499 follow-up: legacy floor ∪ config-derived ids — see getStringOperatorFilterKeys.
  // Computed once and threaded through both the generic wrap below and the searchable-id exclusion
  // that follows, so the two stay in agreement about which ids own the {operator, values} shape.
  const stringOperatorKeys = getStringOperatorFilterKeys(filterConfigs);

  const fieldsToSpread = Object.fromEntries(
    Object.entries(filterFields)
      .filter(([key]) => !EXCLUDED_FROM_INPUT.has(key))
      .map(([key, value]) => [
        key,
        // KATANA-1509 follow-up: brandIdOperator's GlobalFilters value is already the canonical
        // {operator, values} object (not a flat array — Brand preserves the user's explicit
        // Contains/DoesNotContain choice), so wrapSaveStringOperatorField's Array.isArray check
        // below never matches it and leaves `values` as DropdownOptionFilter[] (label+value
        // objects) instead of flattening to bare ids. Checked first, same as getConfigDrivenFilterVars
        // and buildCustomFilterPayload, so shareable-link saves flatten it identically to those
        // two paths instead of passing dropdown objects through to the GQL wire shape.
        isOperatorFilterValue(value)
          ? { operator: toStringOperator(value.operator), values: value.values.map(toFilterId) }
          : wrapSaveStringOperatorField(key, value, stringOperatorKeys),
      ])
  );

  // Searchable config-driven filters store FilterOption[] ({value, label}) in GlobalFilters
  // but TraffickingFiltersInput expects String[]. Derive the set of searchable filter IDs
  // from the config so no per-field hardcoding is needed here. This overwrites v2AliasFields
  // entries for traffickerEmailList/accountExecutiveList when filterConfigs are available,
  // producing the same normalized result.
  //
  // Excludes stringOperatorKeys ids (e.g. agencyIdList/industryIdList, and once config-driven,
  // buyerNameList/omsPackageNameList): they are also isSearchable, and without this exclusion this
  // flatten-only path would spread after fieldsToSpread and silently overwrite the correctly
  // wrapped {operator, values} from wrapSaveStringOperatorField above with a flat array.
  const searchableIds = new Set(
    filterConfigs.filter((config) => config.isSearchable && !stringOperatorKeys.has(config.id)).map((c) => c.id)
  );
  const normalizedSearchableFields = Object.fromEntries(
    [...searchableIds].map((id) => {
      const value = filters[id as keyof GlobalFilters];
      if (!Array.isArray(value)) return [id, value];
      return [id, (value as (string | DropdownOptionFilter)[]).map(toFilterId)];
    })
  );

  return {
    ...fieldsToSpread,
    ...v2AliasFields,
    ...normalizedSearchableFields,
    ...getLineItemStatusFiltersInput(filters.lineItemStatusList),
    selectedTab: filters.selectedTab ?? TraffickingTableName.campaigns,
    dateFilter: null,
    lineItemEndDate: isLineItemEndDateFilterFilled(filters.lineItemEndDate) ? filters.lineItemEndDate : null,
    percentPacing: isRangeFilterFilled(filters.percentPacing) ? filters.percentPacing : null,
    completionsRemaining: isRangeFilterFilled(filters.completionsRemaining) ? filters.completionsRemaining : null,
    CPM: isRangeFilterFilled(filters.CPM) ? filters.CPM : null,
  };
};
